import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

/**
 * Tooltip shadcn + 2 variantes produit :
 * - `action` : fond noir (boutons / actions)
 * - `info` : fond blanc bordure primaire (nature des champs)
 *
 * Pointe `info` : uniquement border-r + border-b (après rotate-45 = arêtes
 * externes vers le trigger). Pas de contour complet sur le losange.
 */
const tooltipContentVariants = cva(
  "z-50 w-fit rounded-md px-3 py-1.5 text-xs animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]",
  {
    variants: {
      variant: {
        default: "bg-background text-primary-foreground text-balance",
        // Types 1/2 : largeur max 328px, texte justifié, pas de césure.
        action:
          "max-w-[328px] bg-black text-white rounded-xl text-justify hyphens-none [overflow-wrap:normal] [word-break:normal]",
        info: "max-w-[328px] bg-white border border-primary text-primary shadow-sm text-justify hyphens-none [overflow-wrap:normal] [word-break:normal]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const tooltipArrowVariants = cva(
  "z-50 size-2.5 translate-y-[calc(-50%_-_0px)] rotate-45 rounded-[2px]",
  {
    variants: {
      variant: {
        default: "bg-background fill-background",
        action: "bg-black fill-black",
        info: "bg-white fill-white border-r border-b border-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export type TooltipVariant = NonNullable<
  VariantProps<typeof tooltipContentVariants>["variant"]
>;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    arrowClassName?: string;
  } & VariantProps<typeof tooltipContentVariants>
>(
  (
    {
      className,
      arrowClassName,
      sideOffset = 4,
      children,
      variant = "default",
      ...props
    },
    ref,
  ) => (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(tooltipContentVariants({ variant }), className)}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className={cn(tooltipArrowVariants({ variant }), arrowClassName)}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  ),
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  tooltipContentVariants,
};
